import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import {
  addCompaniesToList,
  addPeopleToList,
  bulkAddListItems,
  copyItemsToLists,
  bulkRemoveListItems,
  createSourcingSavedSearch,
  fetchListCompanies,
  fetchListPeople,
  getSourcingListsAndSavedSearches,
} from '../queries/SourcingListsQueries';
import { getAgentSearchResultsById, getSourcingSearchById } from '../queries/SourcingSearchQueries';
import { IAddCompanyToListRequest, IAddPersonToListRequest } from '../../schemas/SourcingListItems.schema';
import { ScheduleValue } from '../../schemas/SourcingList.schema';

export const SOURCING_LISTS_KEY = 'sourcing-lists';
export const SOURCING_LIST_COMPANIES_KEY = 'sourcing-list-companies';
export const SOURCING_LIST_PEOPLE_KEY = 'sourcing-list-people';
export const SOURCING_SEARCH_BY_ID_KEY = 'sourcing-search-by-id';
export const AGENT_SEARCH_RESULTS_BY_ID_KEY = 'agent-search-results-by-id';
// Membership lookup for the company-profile page (`GET /api/v1/company-profile/lists`).
// Lives next to the other list keys so mutations in this file can invalidate
// it without crossing the services → pages dependency boundary.
export const SOURCING_COMPANY_LISTS_KEY = 'sourcing-company-lists';

const FIVE_MINUTES = 5 * 60 * 1000;

export function useSourcingListsQuery() {
  const { pushErrorToast } = useToastMessageState();

  return useQuery({
    queryKey: [SOURCING_LISTS_KEY],
    queryFn: async () => {
      try {
        return await getSourcingListsAndSavedSearches();
      } catch (error) {
        pushErrorToast({ message: 'Failed to load sourcing data. Please try again.' });
        throw error;
      }
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useListCompaniesQuery(listId: string | undefined, entityType: 'company' | 'person') {
  return useQuery({
    queryKey: [SOURCING_LIST_COMPANIES_KEY, listId, entityType],
    queryFn: ({ signal }) => fetchListCompanies(listId!, signal),
    enabled: Boolean(listId) && entityType === 'company',
    staleTime: FIVE_MINUTES,
  });
}

export function useListPeopleQuery(listId: string | undefined, entityType: 'company' | 'person') {
  return useQuery({
    queryKey: [SOURCING_LIST_PEOPLE_KEY, listId, entityType],
    queryFn: ({ signal }) => fetchListPeople(listId!, signal),
    enabled: Boolean(listId) && entityType === 'person',
    staleTime: FIVE_MINUTES,
  });
}

export function useSourcingSearchByIdQuery(searchId: string | undefined) {
  return useQuery({
    queryKey: [SOURCING_SEARCH_BY_ID_KEY, searchId],
    queryFn: () => getSourcingSearchById(searchId!),
    enabled: Boolean(searchId),
  });
}

export function useBulkRemoveListItemsMutation(listId: string | undefined, entityType: 'company' | 'person') {
  const queryClient = useQueryClient();
  const { pushErrorToast } = useToastMessageState();
  const queryKey =
    entityType === 'company'
      ? [SOURCING_LIST_COMPANIES_KEY, listId, entityType]
      : [SOURCING_LIST_PEOPLE_KEY, listId, entityType];

  return useMutation({
    mutationFn: (itemIds: string[]) => {
      if (!listId) {
        throw new Error('Cannot remove items without a listId');
      }
      return bulkRemoveListItems(listId, itemIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to remove items. Please try again.' });
    },
  });
}

export function useAgentSearchResultsByIdQuery(searchId: string | undefined) {
  return useQuery({
    queryKey: [AGENT_SEARCH_RESULTS_BY_ID_KEY, searchId],
    queryFn: () => getAgentSearchResultsById(searchId!),
    enabled: Boolean(searchId),
  });
}

export function useAddCompaniesToListMutation(listId: string | undefined) {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (companies: IAddCompanyToListRequest[]) => {
      if (!listId) {
        throw new Error('Cannot add companies without a listId');
      }
      return addCompaniesToList(listId, companies);
    },
    onSuccess: (_data, companies) => {
      queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_COMPANIES_KEY, listId, 'company'] });
      // Refresh the profile-page membership lookup so "Added to N lists" and
      // the dialog's already-saved filter both update after this mutation.
      queryClient.invalidateQueries({ queryKey: [SOURCING_COMPANY_LISTS_KEY] });
      const name = companies[0]?.name;
      pushSuccessToast({ message: name ? `"${name}" added to list` : 'Company added to list' });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to add company. Please try again.' });
    },
  });
}

export function useAddPeopleToListMutation(listId: string | undefined) {
  const queryClient = useQueryClient();
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (people: IAddPersonToListRequest[]) => {
      if (!listId) {
        throw new Error('Cannot add people without a listId');
      }
      return addPeopleToList(listId, people);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_PEOPLE_KEY, listId, 'person'] });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to add person. Please try again.' });
    },
  });
}

export function useCopyItemsToListsMutation() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ itemIds, targetListIds }: { itemIds: string[]; targetListIds: string[] }) => {
      return copyItemsToLists(itemIds, targetListIds);
    },
    onSuccess: (_data, { itemIds, targetListIds }) => {
      targetListIds.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_COMPANIES_KEY, id] });
        queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_PEOPLE_KEY, id] });
      });
      // Profile-header membership counts depend on what's in any list, so a
      // copy-into-list flow must refresh the lookup query for any open profile.
      queryClient.invalidateQueries({ queryKey: [SOURCING_COMPANY_LISTS_KEY] });
      const itemLabel = itemIds.length === 1 ? '1 item' : `${itemIds.length} items`;
      const listLabel = targetListIds.length === 1 ? '1 list' : `${targetListIds.length} lists`;
      pushSuccessToast({ message: `${itemLabel} added to ${listLabel}` });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to add items. Please try again.' });
    },
  });
}

/**
 * Add the same set of entities to multiple lists in a single round trip.
 * Used by the company-profile "Add to List" button: one company →
 * many lists. Pass domains as `entityIds` for company lists (or
 * LinkedIn URLs for person lists, per the API contract).
 */
export function useBulkAddListItemsMutation() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ entityIds, listIds }: { entityIds: string[]; listIds: string[] }) => {
      return bulkAddListItems(entityIds, listIds);
    },
    onSuccess: (_data, { listIds }) => {
      listIds.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_COMPANIES_KEY, id] });
        queryClient.invalidateQueries({ queryKey: [SOURCING_LIST_PEOPLE_KEY, id] });
      });
      // Refresh every open profile's membership count so the header text
      // updates ("Added to N lists") and the picker hides newly-saved lists.
      queryClient.invalidateQueries({ queryKey: [SOURCING_COMPANY_LISTS_KEY] });
      const listLabel = listIds.length === 1 ? '1 list' : `${listIds.length} lists`;
      pushSuccessToast({ message: `Added to ${listLabel}` });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to add to lists. Please try again.' });
    },
  });
}

export function useCreateSavedSearchMutation() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({
      name,
      entityType,
      query,
      schedule,
    }: {
      name: string;
      entityType: 'company' | 'person';
      query?: string;
      schedule?: ScheduleValue;
    }) => createSourcingSavedSearch(name, entityType, query, schedule),
    onSuccess: (_data, { name }) => {
      queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
      pushSuccessToast({ message: `"${name}" saved to Saved Searches.` });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to save search. Please try again.' });
    },
  });
}
